const MICROLINK_API_URL = "https://api.microlink.io";
const THUM_IO_BASE_URL = "https://image.thum.io/get/width/720/crop/768/noanimate/";

type MicrolinkAsset =
  | string
  | {
      url?: string;
    }
  | null
  | undefined;

type MicrolinkResponse = {
  data?: {
    image?: MicrolinkAsset;
    screenshot?: MicrolinkAsset;
  };
};

export type LinkPreviewResult = {
  imageSrc?: string;
  fallbackImageSrc?: string;
};

const previewCache = new Map<string, Promise<LinkPreviewResult>>();

const IMAGE_URL_PATTERN = /\.(avif|bmp|gif|ico|jpe?g|png|svg|webp)(\?.*)?$/i;

const getAssetUrl = (asset: MicrolinkAsset): string | undefined => {
  if (typeof asset === "string" && asset.trim()) {
    return asset;
  }

  if (
    asset &&
    typeof asset === "object" &&
    typeof asset.url === "string" &&
    asset.url.trim()
  ) {
    return asset.url;
  }

  return undefined;
};

const buildMicrolinkUrl = (href: string) => {
  const url = new URL(MICROLINK_API_URL);
  url.searchParams.set("url", href);
  url.searchParams.set("screenshot", "true");
  url.searchParams.set("meta", "true");
  return url.toString();
};

const buildScreenshotFallbackUrl = (href: string) => {
  return `${THUM_IO_BASE_URL}${encodeURI(href)}`;
};

const dedupeUrls = (items: Array<string | undefined>) => {
  return items.filter((item, index, array): item is string => {
    return Boolean(item) && array.indexOf(item) === index;
  });
};

export const resolveLinkPreview = (href: string): Promise<LinkPreviewResult> => {
  if (!href) {
    return Promise.resolve({});
  }

  if (IMAGE_URL_PATTERN.test(href)) {
    return Promise.resolve({
      imageSrc: href,
    });
  }

  const cachedResult = previewCache.get(href);
  if (cachedResult) {
    return cachedResult;
  }

  const request = fetch(buildMicrolinkUrl(href))
    .then(async (response) => {
      if (!response.ok) {
        throw new Error(`Preview lookup failed with status ${response.status}`);
      }

      const payload = (await response.json()) as MicrolinkResponse;
      const imageSrc = getAssetUrl(payload.data?.image);
      const screenshotSrc = getAssetUrl(payload.data?.screenshot);
      const [primaryImageSrc, fallbackImageSrc] = dedupeUrls([
        imageSrc,
        screenshotSrc,
        buildScreenshotFallbackUrl(href),
      ]);

      return {
        imageSrc: primaryImageSrc,
        fallbackImageSrc,
      };
    })
    .catch(() => {
      return {
        fallbackImageSrc: buildScreenshotFallbackUrl(href),
      };
    });

  previewCache.set(href, request);
  return request;
};
